import { useEffect, useRef, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Camera, AlertCircle, Mail, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, refreshUser } = useAuth();
  const hasProcessed = useRef(false);
  const [suspended, setSuspended] = useState(false);
  const [suspendedMessage, setSuspendedMessage] = useState("");

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Check for success from custom OAuth callback
      const success = searchParams.get("success");
      
      if (success === "true") {
        // Custom OAuth flow - session cookie is already set
        try {
          // Verify session and get user data
          const response = await axios.get(`${API}/auth/me`, {
            withCredentials: true
          });
          
          login(response.data);
          toast.success("Welcome to 360Lumina!");
          
          // Clean URL and redirect
          window.history.replaceState(null, "", "/dashboard");
          navigate("/dashboard", { replace: true });
          return;
        } catch (error) {
          console.error("Auth verification error:", error);
          toast.error("Authentication failed");
          navigate("/login");
          return;
        }
      }
      
      // Legacy: Extract session_id from URL fragment (Emergent Auth)
      const hash = window.location.hash;
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];

      if (!sessionId) {
        toast.error("Authentication failed");
        navigate("/login");
        return;
      }

      try {
        // Exchange session_id for session_token
        const response = await axios.get(`${API}/auth/session`, {
          headers: { "X-Session-ID": sessionId },
          withCredentials: true
        });

        login(response.data);
        toast.success("Welcome to 360Lumina!");
        
        // Clean URL and redirect
        window.history.replaceState(null, "", "/dashboard");
        navigate("/dashboard", { replace: true });
      } catch (error) {
        console.error("Auth error:", error);
        
        // Check if account is suspended (403 status)
        if (error.response?.status === 403) {
          setSuspended(true);
          setSuspendedMessage(error.response.data?.detail || "Your account has been suspended.");
        } else {
          toast.error("Authentication failed");
          navigate("/login");
        }
      }
    };

    processAuth();
  }, [navigate, login, searchParams]);

  // Parse suspended message for contact info
  const parseContactInfo = () => {
    if (!suspendedMessage) return { message: "", email: "", phone: "", contact: "" };
    
    const parts = suspendedMessage.split("|").map(p => p.trim());
    const mainMessage = parts[0]?.split("Contact:")[0]?.trim() || "Your account has been suspended.";
    
    let email = "";
    let phone = "";
    let contact = "";
    
    for (const part of parts) {
      if (part.includes("Email:")) {
        email = part.replace("Email:", "").trim();
      } else if (part.includes("Phone:")) {
        phone = part.replace("Phone:", "").trim();
      } else if (part.includes("Contact:")) {
        contact = part.replace("Contact:", "").trim();
      }
    }
    
    // Also check the main part
    if (suspendedMessage.includes("Contact:") && !contact) {
      const contactMatch = suspendedMessage.match(/Contact:\s*([^|]+)/);
      if (contactMatch) contact = contactMatch[1].trim();
    }
    
    return { message: mainMessage, email, phone, contact };
  };

  if (suspended) {
    const contactInfo = parseContactInfo();
    
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <Card className="max-w-md w-full bg-card border-border">
          <CardContent className="pt-8 pb-6 text-center">
            <div className="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-destructive" />
            </div>
            
            <h1 className="text-2xl font-serif mb-2">Account Suspended</h1>
            <p className="text-muted-foreground mb-6">
              {contactInfo.message}
            </p>
            
            {(contactInfo.contact || contactInfo.email || contactInfo.phone) && (
              <div className="bg-muted/30 rounded-sm p-4 mb-6 text-left">
                <p className="text-sm font-medium mb-3">Contact to Reactivate:</p>
                {contactInfo.contact && (
                  <p className="text-sm text-muted-foreground mb-2">{contactInfo.contact}</p>
                )}
                {contactInfo.email && (
                  <a 
                    href={`mailto:${contactInfo.email}`}
                    className="flex items-center gap-2 text-sm text-primary hover:underline mb-2"
                  >
                    <Mail className="w-4 h-4" />
                    {contactInfo.email}
                  </a>
                )}
                {contactInfo.phone && (
                  <a 
                    href={`tel:${contactInfo.phone}`}
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <Phone className="w-4 h-4" />
                    {contactInfo.phone}
                  </a>
                )}
              </div>
            )}
            
            <Link to="/">
              <Button className="btn-secondary rounded-sm w-full">
                <Camera className="w-4 h-4 mr-2" />
                Return to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="spinner mx-auto mb-4" />
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
